import { CrownIcon, CrossedLimitIcon, ImprovedIcon, NotImprovedIcon, ZeroIcon, TooltipIcons } from 'assests/GmsIcons';
import React, { useContext } from 'react';
import { ABContext } from '../../contexts/ABContext';
import { TABResult } from '../../types/ab';
import CustomChip from '../../components/CustomChip';
import { NORMALIZED } from '../../utils/Constants';
import OverlayIcon from 'Components/GMS/Rules/Components/OverlayIcon';
import StatusIndicator from '../../components/StatusIndicator';

const ResultTable = ({ checkBox }: { checkBox?: boolean }) => {
    const abContext = useContext(ABContext);
    const { testData, selectedModelId, setSelectedModelId } = abContext

    const getModelTypeList = () => {
        let index = 1;
        const sortedData = (testData?.result || []).sort((a: TABResult, b: TABResult) => {
            if (a.is_control && !b.is_control) return -1;
            if (!a.is_control && b.is_control) return 1;
            return 0;
        });

        const returnData = sortedData.map((x: TABResult) => {
            let name = x?.name ?? '';
            if (x?.is_control) {
                name = `Control: ${x?.name ?? ''}`;
            } else {
                name = `Variant ${index}: ${x?.name ?? ''}`;
                index += 1;
            }
            return { ...x, name };
        });

        return returnData;
    }

    const getIcon = (status: string, rank?: number) => {
        if (!status) return null;

        const config = {
            won: {
                icon: <CrownIcon />,
                text: ''
            },
            crossed_limit: {
                icon: <CrossedLimitIcon />,
                text: ''
            },
            improved: {
                icon: <ImprovedIcon />,
                text: ''
            },
            not_improved: {
                icon: <NotImprovedIcon />,
                text: ''
            },
            zero: {
                icon: <ZeroIcon />,
                text: ''
            }
        };

        const result = config[status as keyof typeof config];

        if (!result) return null;

        return (
            <div className="tw-flex tw-items-center tw-gap-1">
                {result.icon}
                <span className="tw-text-sm tw-text-[#090A42]">{result.text}</span>
            </div>
        );
    };

    return (
        <div>
            <div className='tw-w-fit tw-ms-2'>
                <StatusIndicator message={testData?.metric ?? ''} placeholder={`Metric:`} />
            </div>
            <div className="tw-overflow-x-auto tw-m-2">
                <table className="bg-white tw-min-w-full">
                    <colgroup>
                        {checkBox ? <col className="tw-w-12" /> : null}
                    </colgroup>
                    <thead >
                        <tr>
                            {checkBox ? <th className="tw-text-[#6B778C] tw-font-semibold tw-text-sm tw-text-left tw-pl-4"></th> : null}
                            <th className="tw-text-[#6B778C] tw-font-semibold tw-text-sm tw-text-left tw-pl-4">Models</th>
                            <th className="tw-text-[#6B778C] tw-font-semibold tw-text-sm tw-text-left tw-pl-4">Value</th>
                            <th className="tw-text-[#6B778C] tw-font-semibold tw-text-sm tw-text-left tw-pl-4">Session count</th>
                            <th className="tw-text-[#6B778C] tw-font-semibold tw-text-sm tw-text-left tw-pl-4">Value Per session</th>
                            <th className="tw-text-[#6B778C] tw-font-semibold tw-text-sm tw-text-left tw-pl-4">
                                <div className='tw-flex tw-flex-wrap tw-items-center tw-gap-1'>
                                    {`Normalized Value`}
                                    
                                    {!checkBox ? <OverlayIcon message={<>
                                        {NORMALIZED}
                                    </>} /> : null}
                                </div>
                            </th>
                            <th className="tw-text-[#6B778C] tw-font-semibold tw-text-sm tw-text-left tw-pl-4">Improvement</th>
                            <th className="tw-text-[#6B778C] tw-font-semibold tw-text-sm tw-text-left tw-pl-4">
                                <div className='tw-flex tw-items-center tw-gap-1'>
                                    {'Result'}
                                    {!checkBox ? <span> <OverlayIcon message={
                                        <>
                                            <TooltipIcons />
                                        </>
                                    } />
                                    </span> : null}
                                </div>
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            {checkBox && <td className="tw-h-4"></td>}
                            <td className="tw-h-4"></td>
                            <td className="tw-h-4"></td>
                            <td className="tw-h-4"></td>
                            <td className="tw-h-4"></td>
                        </tr>
                        {getModelTypeList()?.map((res: TABResult, i: number) =>
                            <tr className={`tw-h-12  ${i === 0 ? 'tw-bg-[#f4f5f7]' : ''}`} key={res?.id}>
                                {checkBox ? <td className="tw-text-left tw-pl-4">
                                    <input type="radio" name={`radio-group-${i}`} checked={selectedModelId === res?.id} onChange={() => {
                                        setSelectedModelId(res?.id)
                                    }} />
                                </td> : null}
                                <td className="tw-text-[#090A42] tw-text-sm tw-text-left tw-pl-4">
                                    <div className='tw-flex tw-items-center tw-gap-2'>
                                        <p>{res?.name}</p>
                                        {res?.is_promoted ?
                                            <CustomChip status="Promoted" />
                                            : null}
                                    </div>
                                </td>
                                <td className="tw-text-[#090A42] tw-text-sm tw-text-left tw-pl-4">{res?.performance?.toFixed(2) ?? '-'}</td>
                                <td className="tw-text-[#090A42] tw-text-sm tw-text-left tw-pl-4">{res?.session_count ?? '0'}</td>
                                <td className="tw-text-[#090A42] tw-text-sm tw-text-left tw-pl-4">{res?.performance_per_session?.toFixed(2) ?? '0.00'}</td>
                                <td className="tw-text-[#090A42] tw-text-sm tw-text-left tw-pl-4">{res?.normalised_performance?.toFixed(2) ?? '-'}</td>
                                <td className="tw-text-[#090A42] tw-text-sm tw-text-left tw-pl-4">
                                    {res?.is_control ? 'Baseline' : res?.improvement != null ? `${res.improvement?.toFixed(2)} %` : '-'}
                                </td>
                                <td className="tw-h-12">
                                    <div className="tw-flex tw-items-center tw-pl-4">
                                        {!res?.is_control && getIcon(res?.result_status, res?.rank)}
                                    </div>
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default ResultTable;